/*
Next.js executes top-level code at build time. See https://github.com/vercel/next.js/discussions/16840 for further
examples.  Use NEXT_PHASE to avoid Bugsnag.start being executed during the build phase.  See
https://nextjs.org/docs/app/api-reference/config/next-config-js and
https://github.com/vercel/next.js/blob/canary/packages/next/src/shared/lib/constants.ts#L26 for more details on
NEXT_PHASE
*/

import type { NotifiableError, OnErrorCallback } from '@bugsnag/js';
import { hasWindow, isTest, nodeVersion, provider, runtime } from 'std-env';
import { Env } from '@/libs/Env';

let bugsnag: typeof import('@bugsnag/js').default | null = null;

const isServer = () => !hasWindow;

export const getBugsnag = () => bugsnag;

export const startBugsnag = async () => {
  if (process.env.NEXT_PHASE === 'phase-production-build') {
    return null;
  }

  if (bugsnag) {
    return bugsnag;
  }

  try {
    if (isServer()) {
      return await startServerBugsnag();
    } else {
      return await startClientBugsnag();
    }
  } catch (e) {
    // Avoid logging noisy 'navigator is not defined' error
    if (!(e instanceof ReferenceError)) {
      console.error('Error starting Bugsnag', e);
    }

    return null;
  }
};

export const startServerBugsnag = async () => {
  if (Env.BUGSNAG_SERVER_API_KEY === '' || Env.NEXT_PUBLIC_APP_ENVIRONMENT === 'test' || isTest) {
    return null;
  }
  console.info('Starting Bugsnag server');

  bugsnag = await loadBugsnag();

  bugsnag.start({
    apiKey: Env.BUGSNAG_SERVER_API_KEY,
    appVersion: process.env.VERCEL_GIT_COMMIT_SHA,
    releaseStage: Env.NEXT_PUBLIC_APP_ENVIRONMENT,
    enabledReleaseStages: ['preview', 'staging', 'production'],
    redactedKeys: [/^password$/i, '/token'],
    onError: (event) => {
      // https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/
      if (event.errors.some(error => error.errorClass === 'CustomIgnoreError')) {
        console.warn('CustomIgnoreError detected - skipping error reporting as requested');
        return false;
      }

      event.addMetadata('environment', {
        nodeVersion,
        provider,
        runtime,
      });
      return true;
    },
  });

  console.info('Bugsnag server started');

  return bugsnag;
};

export const startClientBugsnag = async () => {
  if (Env.NEXT_PUBLIC_BUGSNAG_CLIENT_API_KEY === '' || Env.NEXT_PUBLIC_APP_ENVIRONMENT === 'test' || isTest) {
    return null;
  }
  console.info('Starting Bugsnag client');

  bugsnag = await loadBugsnag();
  const BugsnagPluginReact = (await import('@bugsnag/plugin-react')).default;

  bugsnag.start({
    apiKey: Env.NEXT_PUBLIC_BUGSNAG_CLIENT_API_KEY,
    appVersion: process.env.NEXT_BUILD_ID,
    releaseStage: Env.NEXT_PUBLIC_APP_ENVIRONMENT,
    enabledReleaseStages: ['preview', 'staging', 'production'],
    plugins: [new BugsnagPluginReact()],
    redactedKeys: [/^password$/i, '/token'],
    onError: (event) => {
      // https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/
      if (event.errors.some(error => error.errorClass === 'CustomIgnoreError')) {
        console.warn('CustomIgnoreError detected - skipping error reporting as requested');
        return false;
      }
      return true;
    },
  });

  console.info('Bugsnag client started');

  return bugsnag;
};

export const loadBugsnag = async () => (await import('@bugsnag/js')).default;

export const reportError = async (error: NotifiableError, onError?: OnErrorCallback): Promise<void> => {
  if (!bugsnag) {
    await startBugsnag();
  }

  if (bugsnag) {
    bugsnag.notify(error, onError);
  } else {
    console.error('Bugsnag was not initialized', error);
  }
};

export const getClientIp = (req: Request): string => {
  const xForwardedFor = req.headers.get('x-forwarded-for');
  if (xForwardedFor) {
    const ips = xForwardedFor.split(',');
    if (ips.length > 0 && ips[0]) {
      return ips[0].trim();
    }
  }

  const xRealIP = req.headers.get('x-real-ip');
  if (xRealIP) {
    return xRealIP.trim();
  }

  const cfConnectingIP = req.headers.get('cf-connecting-ip');
  if (cfConnectingIP) {
    return cfConnectingIP.trim();
  }

  return '';
};

export const withErrorReporting = async (request: Request, func: (request: Request) => Promise<Response>) => {
  try {
    return await func(request);
  } catch (error) {
    if (error instanceof Error && isServer()) {
      await reportError(error, (event) => {
        event.context = 'API Request';
        event.request = {
          clientIp: getClientIp(request),
          headers: Object.fromEntries(request.headers),
          httpMethod: request.method,
          referer: request.referrer,
          url: request.url,
        };
      });
    }
    throw error;
  }
};
